import { plural } from "@/lib/plural";
import type { Verdict } from "@patent-studio/api-types";
import type { Tone } from "@/components/ui/tone";

/**
 * The words the tab uses for each verdict. Keyed by the served type rather
 * than listed, so a verdict added to the enum fails to compile here instead
 * of rendering as its wire value.
 */
export const VERDICT_LABELS: Record<Verdict, string> = {
  novel: "Novel",
  partially_novel: "Partially Novel",
  not_novel: "Not Novel",
  not_assessed: "Not assessed for novelty",
};

/**
 * What "clear" means, said wherever the page tells the attorney a position is
 * clear of the art: novelty over the attorney's references read, and nothing
 * about obviousness. The server's headline says it in the same words.
 */
export function clearOf(referencesRead: number): string {
  return `Clear of the ${plural(referencesRead, "reference")} read; obviousness is not assessed`;
}

/** What each verdict means, in the story's words: the key the glance opens. */
export const VERDICT_MEANINGS: Record<Exclude<Verdict, "novel">, string> = {
  partially_novel:
    "An aspect of the position is disclosed in the prior art but no single document reaches the whole of it.",
  not_novel:
    "The position is directly disclosed in one or more prior art documents.",
  not_assessed:
    "The position has not been checked against the prior art library as it now reads.",
};

/**
 * What a verdict means over the documents a run read. A novel position is
 * clear only of those, so its meaning carries the count.
 */
export function verdictMeaning(
  verdict: Verdict,
  referencesRead: number,
): string {
  return verdict === "novel"
    ? `${clearOf(referencesRead)}.`
    : VERDICT_MEANINGS[verdict];
}

/** The three verdicts a run hands down, in the order the glance counts them. */
export const RULED_VERDICTS = [
  "novel",
  "partially_novel",
  "not_novel",
] as const;

/** The tint each verdict is drawn in, so the three are told apart at a glance. */
export const VERDICT_TONE: Record<Verdict, Tone> = {
  novel: "success",
  partially_novel: "warning",
  not_novel: "destructive",
  not_assessed: "neutral",
};

/**
 * The tint each verdict is drawn in on the design's dark tip, where a surface
 * tone would disappear. Beside `VERDICT_TONE` so a verdict added to the enum
 * has to answer for both grounds at once.
 */
export const VERDICT_ON_TIP: Record<Verdict, string> = {
  novel: "text-success-on-tip",
  partially_novel: "text-warning-on-tip",
  not_novel: "text-destructive-on-tip",
  not_assessed: "text-white/70",
};
